import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Check as CheckIcon } from '../../../icons/check';

import { getDotStyles } from './utils/get-dot-styles';
import { getTextColor } from './utils/get-text-color';
import { marginLines } from './utils/margin-lines';
import { skewLines } from './utils/skew-lines';

export interface Step {
  title?: string;
  skewLine?: string;
  step: string;
  isSubStep?: boolean;
}

type StepperOrientation = 'vertical' | 'horizontal';

export enum StepValue {
  COMPLETE = 'complete',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

interface Context {
  step: string;
  content: string;
  subSteps?: {
    [key: string]: Step[];
  };
}

interface Props {
  steps: Step[];
  orientation?: StepperOrientation;
  context: Context;
}

const mapSteps = (steps: Step[], currentStep: number) => {
  return steps.map(item => {
    if (currentStep > +item.step) {
      return { ...item, value: StepValue.COMPLETE };
    }

    if (currentStep === +item.step) {
      return { ...item, value: StepValue.ACTIVE };
    }

    return { ...item, value: StepValue.INACTIVE };
  });
};

export const Stepper = ({ steps, orientation, context }: Props) => {
  const { t } = useTranslation(['common']);

  const stepsArray = [...steps];

  const [step] = context.step.toString().split('.');

  if ((context?.subSteps?.[step]?.length ?? 0) > 0) {
    stepsArray.splice(+step, 0, ...(context?.subSteps?.[step] ?? []));
  }

  const mappedSteps = mapSteps(context?.subSteps ? stepsArray : steps, +context.step);

  return (
    <Timeline
      sx={{
        flexDirection: orientation === 'vertical' ? 'column' : 'row',
        flexGrow: 'inherit',
        my: 0,
        p: 0,
      }}>
      {mappedSteps.map((item, index) => {
        return (
          <Box
            key={item.title}
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: orientation === 'vertical' ? 'column' : 'row',
              alignItems: orientation === 'vertical' ? 'inherit' : 'center',
              ml: item?.isSubStep ? 2 : 0,
            }}>
            <TimelineItem
              sx={{
                alignItems: 'center',
                minHeight: 'auto',
                '&::before': {
                  display: 'none',
                },
              }}>
              <TimelineDot
                sx={{
                  ...getDotStyles(item.value),
                  alignItems: 'center',
                  alignSelf: 'center',
                  boxShadow: 'none',
                  display: 'flex',
                  flexShrink: 0,
                  height: item?.isSubStep ? 30 : 36,
                  justifyContent: 'center',
                  m: 0,
                  width: item?.isSubStep ? 30 : 36,
                }}
                variant={item.value === StepValue.COMPLETE ? 'filled' : 'outlined'}>
                {item.value === StepValue.ACTIVE ? (
                  <Box
                    sx={{
                      backgroundColor: 'primary.main',
                      borderRadius: '50%',
                      height: 12,
                      width: 12,
                    }}
                  />
                ) : (
                  item.value === StepValue.COMPLETE && (
                    <CheckIcon sx={{ color: 'primary.contrastText' }} />
                  )
                )}
              </TimelineDot>
              <TimelineContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  pr: 1,
                }}>
                <Typography color={getTextColor(item.value).title} variant="overline">
                  {t('common:step') + ' ' + item.step}
                </Typography>
                <Typography color={getTextColor(item.value).title} variant="body2">
                  {item.title}
                </Typography>
              </TimelineContent>
            </TimelineItem>
            {mappedSteps.length > index + 1 && (
              <TimelineConnector
                sx={{
                  backgroundColor:
                    item.value === StepValue.COMPLETE ? 'primary.main' : 'neutral.200',
                  height: orientation === 'vertical' ? 22 : 2,
                  width: orientation === 'vertical' ? 2 : 22,
                  my: orientation === 'vertical' ? 1 : 'inherit',
                  ml: marginLines(mappedSteps, index),
                  mr: orientation === 'vertical' ? 0 : 1,
                  transform: skewLines(mappedSteps, index),
                }}
              />
            )}
          </Box>
        );
      })}
    </Timeline>
  );
};
